import { useMemo, useState } from 'react';
import { RUNNING_SURFACES, RUN_TYPE_LABELS, formatPaceLabel, paceSecFromMinSec } from '../../utils/trainingSportSession';
import { WEEKDAYS, nextWeekday, sequentialWeekdays } from '../../utils/trainingWeekday';
import {
  RUN_GOALS,
  RUN_LEVELS,
  applyRunningRowEdits,
  generateRunningWeekPlan,
} from '../../utils/runningPlanGenerator';

/** Modal coach: arma una semana Running (video de técnica se llena después, opcional). */
export default function RunningPlanWizard({ open, onClose, onApply, applying = false }) {
  const [goal, setGoal] = useState('10k');
  const [level, setLevel] = useState('intermedio');
  const [paceMin, setPaceMin] = useState('5');
  const [paceSec, setPaceSec] = useState('30');
  const [surface, setSurface] = useState('asphalt');
  const [weekdays, setWeekdays] = useState(() => sequentialWeekdays(4, 'monday'));
  const [rowEdits, setRowEdits] = useState({});
  const daysPerWeek = weekdays.length;

  const setDaysCount = (n) => {
    const count = Math.min(7, Math.max(1, Number(n) || 1));
    setWeekdays((prev) => {
      if (prev.length === count) return prev;
      if (prev.length > count) return prev.slice(0, count);
      const start = nextWeekday(prev[prev.length - 1] || 'monday');
      return [...prev, ...sequentialWeekdays(count - prev.length, start)];
    });
    if (count < daysPerWeek) {
      setRowEdits((prev) => {
        const next = {};
        Object.keys(prev).forEach((k) => {
          const i = Number(k);
          if (i < count) next[i] = prev[k];
        });
        return next;
      });
    }
  };

  const patchRow = (idx, field, value) => {
    setRowEdits((prev) => ({
      ...prev,
      [idx]: { ...(prev[idx] || {}), [field]: value },
    }));
  };

  const removeRow = (idx) => {
    if (weekdays.length <= 1) return;
    setWeekdays((prev) => prev.filter((_, i) => i !== idx));
    setRowEdits((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const i = Number(k);
        if (i < idx) next[i] = prev[k];
        else if (i > idx) next[i - 1] = prev[k];
      });
      return next;
    });
  };

  const generated = useMemo(() => generateRunningWeekPlan({
    goal,
    level,
    daysPerWeek,
    easyPaceSecPerKm: paceSecFromMinSec(paceMin, paceSec) || 330,
    surface,
    weekdays,
  }), [goal, level, daysPerWeek, paceMin, paceSec, surface, weekdays]);

  const preview = useMemo(
    () => applyRunningRowEdits(generated, rowEdits),
    [generated, rowEdits],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 bg-black/40">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl border border-stone-200 bg-white ui-light-panel surface-readable training-light-island p-4 sm:p-5 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-bold text-stone-900 m-0">Añadir días Running</h3>
            <p className="text-xs text-stone-600 mt-1 mb-0">
              Suma 1–7 sesiones al plan sin borrar gimnasio ni natación. Edita km programados y notas del entrenador en cada fila.
            </p>
          </div>
          <button type="button" className="btn-secondary text-xs" onClick={onClose} disabled={applying}>
            Cerrar
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-bold uppercase text-stone-500">Objetivo</span>
            <select className="input mt-1 w-full text-sm" value={goal} onChange={(e) => setGoal(e.target.value)}>
              {RUN_GOALS.map((g) => (
                <option key={g.id} value={g.id}>{g.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase text-stone-500">Nivel</span>
            <select className="input mt-1 w-full text-sm" value={level} onChange={(e) => setLevel(e.target.value)}>
              {RUN_LEVELS.map((g) => (
                <option key={g.id} value={g.id}>{g.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase text-stone-500">Días por semana</span>
            <select
              className="input mt-1 w-full text-sm"
              value={daysPerWeek}
              onChange={(e) => setDaysCount(Number(e.target.value))}
            >
              {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                <option key={n} value={n}>{n} {n === 1 ? 'día' : 'días'}</option>
              ))}
            </select>
          </label>
          <div>
            <span className="text-xs font-bold uppercase text-stone-500">Pace fácil (min:seg / km)</span>
            <div className="flex gap-2 mt-1">
              <input
                type="number"
                min={2}
                max={12}
                className="input w-full text-sm h-9"
                value={paceMin}
                onChange={(e) => setPaceMin(e.target.value)}
              />
              <span className="self-center text-stone-500">:</span>
              <input
                type="number"
                min={0}
                max={59}
                className="input w-full text-sm h-9"
                value={paceSec}
                onChange={(e) => setPaceSec(e.target.value)}
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <span className="text-xs font-bold uppercase text-stone-500">Superficie</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {RUNNING_SURFACES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={surface === s.id ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                  onClick={() => setSurface(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-stone-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[44rem]">
            <thead className="bg-stone-50 text-left text-xs uppercase text-stone-500">
              <tr>
                <th className="px-3 py-2">Día</th>
                <th className="px-3 py-2">Día de la semana</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Km</th>
                <th className="px-3 py-2">Pace</th>
                <th className="px-3 py-2 min-w-[12rem]">Notas del entrenador</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {preview.map((d, idx) => (
                <tr key={`${d.dia}-${idx}`} className="border-t border-stone-100 align-top">
                  <td className="px-3 py-2 text-stone-800 whitespace-nowrap">{d.nombre}</td>
                  <td className="px-3 py-2">
                    <select
                      className="input text-sm h-8 py-0 min-w-[8.5rem]"
                      aria-label={`Día de la semana para ${d.nombre || `Día ${d.dia}`}`}
                      value={weekdays[idx] || d.weekday || 'monday'}
                      onChange={(e) => {
                        const next = e.target.value;
                        setWeekdays((prev) => prev.map((w, i) => (i === idx ? next : w)));
                      }}
                    >
                      {WEEKDAYS.map((w) => (
                        <option key={w.id} value={w.id}>{w.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-stone-600 whitespace-nowrap">
                    {RUN_TYPE_LABELS[d.sport_prescription.run_type] || d.sport_prescription.run_type}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0.5}
                      max={80}
                      step={0.1}
                      className="input w-[4.5rem] text-sm h-8 py-0 text-stone-900"
                      aria-label={`Kilómetros programados para ${d.nombre || `Día ${d.dia}`}`}
                      value={d.sport_prescription.distance_target_km ?? ''}
                      onChange={(e) => patchRow(idx, 'km', e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatPaceLabel(d.sport_prescription.pace_target_sec_per_km)}</td>
                  <td className="px-3 py-2">
                    <textarea
                      className="input w-full text-sm min-h-[2.5rem] py-1 text-stone-900 resize-y"
                      rows={2}
                      maxLength={4000}
                      aria-label={`Notas del entrenador para ${d.nombre || `Día ${d.dia}`}`}
                      value={d.coach_notes || ''}
                      placeholder="Indicaciones para el deportista…"
                      onChange={(e) => patchRow(idx, 'notes', e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-xs font-semibold text-red-700 hover:underline disabled:opacity-40"
                      disabled={weekdays.length <= 1 || applying}
                      onClick={() => removeRow(idx)}
                    >
                      Quitar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className="btn-secondary text-sm mr-auto"
            disabled={applying || weekdays.length >= 7}
            onClick={() => setDaysCount(weekdays.length + 1)}
          >
            + Añadir un día
          </button>
          <button type="button" className="btn-secondary text-sm" onClick={onClose} disabled={applying}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={applying}
            onClick={() => onApply?.(preview)}
          >
            {applying ? 'Añadiendo…' : 'Añadir estos días al plan'}
          </button>
        </div>
      </div>
    </div>
  );
}
